/*
  PCG live stats (2026-10 header redesign), after the Pop Report's HeaderStatsTicker.

  Fills three things from the public PCG API:
  - [data-pcg-ticker]        the 36px ticker row above the bar (snippets/pcg-live-ticker.liquid);
  - [data-pcg-live-total]    the bar's "● 414,142 graded" (header.liquid, shown from 1440px);
  - [data-pcg-live-summary]  the drawer's "414,142 cards graded · 35,404 in October".

  Same sources and selection rules as pop-report src/lib/headerStats.ts, and only endpoints
  that already answer the NZ origin:
    /v1/counts                          total, graded this month, Pristine 10s
    /v2/catalog/games?scope=population  most graded TCG and sport
    /v2/catalog/sets?scope=population   most graded set (the API sends the most graded first)
    /v1/gradings?limit=200&sort=-completedOn             hot right now + just certified
    /v1/gradings?limit=4&sort=-completedOn&grade=Flawless latest Flawless 10
  A chip whose source fails is left out; if nothing comes back the row hides itself.

  Fetched once per page load and kept in sessionStorage for 60s, so moving between pages
  does not refetch. /v1/counts alone is then refreshed every 30s while the tab is visible and
  shared (window.PCGLive.counts plus a 'pcg:counts' event), so the header total and the home
  page's Population Report box (snippets/pcg-live-stats.liquid) always show the same number. The scroll is CSS (pcg-header.css); it pauses on hover, on focus and with
  the pause button. Under prefers-reduced-motion, and on phones, it shows one chip at a time
  and fades to the next instead.
*/
(function () {
  if (window.PCGLive) return;

  const API = 'https://app.premiercardgrading.com/public/api';
  const POP = 'https://pcgpopreport.com';
  const CACHE_KEY = 'pcg.live.v2'; // v2: items no longer name the grading office
  const CACHE_MS = 60000;
  const PIXELS_PER_SECOND = 38;
  const FADE_HOLD_MS = 4500;
  const FADE_MS = 400;
  const HOT_MIN_COUNT = 3;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const phone = window.matchMedia('(max-width: 749px)');

  const text = (value) => (typeof value === 'string' ? value.trim() : '');
  const count = (value) => (typeof value === 'number' && isFinite(value) && value > 0 ? value : null);
  const rowsOf = (rows) => (Array.isArray(rows) ? rows.filter((row) => row && typeof row === 'object') : []);
  const fmt = (n) => Math.round(n).toLocaleString('en-US');
  const isMint = (code) => text(code).toLowerCase() === 'mint';
  const setSegment = (name) => name.replace(/\//g, '%2F');
  const monthName = () => new Date().toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });

  // The home page's category rule: no category counts as a TCG; Coins is neither.
  const category = (value) => {
    const c = text(value);
    if (c === 'Sports') return 'Sports';
    if (c === 'TCG' || c === '') return 'TCG';
    return null;
  };

  const liveGames = (games) => rowsOf(games).filter((g) => text(g.gameCode) && !isMint(g.gameCode) && g.archived !== true);

  function topGame(games, section) {
    let best = null;
    liveGames(games).forEach((g) => {
      const graded = count(g.cardsGraded);
      if (graded === null || category(g.category) !== section) return;
      if (!best || graded > best.cardsGraded) best = { name: text(g.name) || text(g.gameCode), gameCode: text(g.gameCode), cardsGraded: graded };
    });
    return best;
  }

  function topSet(sets, games) {
    const codes = new Map();
    liveGames(games).forEach((g) => typeof g.id === 'number' && codes.set(g.id, text(g.gameCode)));
    let best = null;
    rowsOf(sets).forEach((s) => {
      const graded = count(s.cardsGraded);
      const gameCode = typeof s.gameId === 'number' ? codes.get(s.gameId) : undefined;
      const setName = text(s.name);
      if (graded === null || !gameCode || !setName || s.archived === true) return;
      if (!best || graded > best.cardsGraded) best = { setName, gameName: text(s.gameName), gameCode, cardsGraded: graded };
    });
    return best;
  }

  function hotSet(rows) {
    const tallies = new Map();
    let windowSize = 0;
    rowsOf(rows).forEach((row) => {
      if (row.completed !== true || !text(row.grade)) return;
      const gameCode = text(row.cardInfo && row.cardInfo.gameCode);
      if (!gameCode || isMint(gameCode) || isMint(row.game)) return;
      windowSize += 1;
      const setName = text(row.cardInfo && row.cardInfo.setName) || text(row.setName);
      if (!setName) return;
      const key = gameCode.toLowerCase() + '\u0000' + setName;
      const tally = tallies.get(key);
      if (tally) tally.count += 1;
      else tallies.set(key, { setName, gameCode, count: 1 });
    });
    let best = null;
    tallies.forEach((t) => {
      if (!best || t.count > best.count) best = t;
    });
    return best && best.count >= HOT_MIN_COUNT ? Object.assign({ window: windowSize }, best) : null;
  }

  function certified(rows) {
    const out = [];
    rowsOf(rows).forEach((row) => {
      const gradedId = text(row.gradedId);
      const grade = text(row.grade);
      if (row.completed !== true || !grade || !gradedId) return;
      if (isMint(row.cardInfo && row.cardInfo.gameCode) || isMint(row.game)) return;
      // An authentication-only card is grade "Authentic" with finalGrade 0: no number then.
      const number = count(row.finalGrade) !== null ? ' ' + String(row.finalGrade).replace(/\.0$/, '') : '';
      out.push({
        gradedId,
        gradeLabel: grade + number,
        cardName: text(row.cardName) || text(row.name),
        gameName: text(row.gameName)
      });
    });
    return out;
  }

  function selectItems(src) {
    const items = [];
    const tcg = topGame(src.games, 'TCG');
    if (tcg) items.push({ key: 'top-tcg', icon: 'trophy', label: 'Most graded TCG', value: tcg.name, figure: fmt(tcg.cardsGraded), detail: '', href: POP + '/' + tcg.gameCode });

    const month = src.counts && typeof src.counts.monthGraded === 'number' && src.counts.monthGraded > 0 ? src.counts.monthGraded : null;
    if (month !== null) items.push({ key: 'month', icon: 'calendar', label: 'Graded in ' + monthName(), value: '', figure: fmt(month), detail: 'cards', href: '' });

    const hot = hotSet(src.feed);
    if (hot) items.push({ key: 'hot', icon: 'flame', label: 'Hot right now', value: hot.setName, figure: '', detail: hot.count + ' of the last ' + hot.window + ' certified', href: POP + '/' + hot.gameCode + '/' + setSegment(hot.setName) });

    const sport = topGame(src.games, 'Sports');
    if (sport) items.push({ key: 'top-sport', icon: 'medal', label: 'Most graded sport', value: sport.name, figure: fmt(sport.cardsGraded), detail: '', href: POP + '/' + sport.gameCode });

    const flawless = certified(src.flawless).find((row) => /^flawless\b/i.test(row.gradeLabel));
    if (flawless && flawless.cardName) items.push({ key: 'flawless', icon: 'sparkles', label: 'Latest Flawless 10', value: flawless.cardName, figure: '', detail: flawless.gameName, href: POP + '/report/' + encodeURIComponent(flawless.gradedId) });

    const set = topSet(src.sets, src.games);
    if (set) items.push({ key: 'top-set', icon: 'layers', label: 'Most graded set', value: set.setName, figure: fmt(set.cardsGraded), detail: set.gameName, href: POP + '/' + set.gameCode + '/' + setSegment(set.setName) });

    const latest = certified(src.feed)[0];
    if (latest && latest.cardName) items.push({ key: 'latest', icon: 'badge', label: 'Just certified', value: latest.gradeLabel + ' · ' + latest.cardName, figure: '', detail: latest.gameName, href: POP + '/report/' + encodeURIComponent(latest.gradedId) });

    const pristine = src.counts ? count(src.counts.pristine10Count) : null;
    if (pristine !== null) items.push({ key: 'pristine', icon: 'gem', label: 'Pristine 10s graded', value: '', figure: fmt(pristine), detail: '', href: '' });

    return items;
  }

  function readCache() {
    try {
      const data = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (data && Date.now() - data.at < CACHE_MS && Array.isArray(data.items)) return data;
    } catch (e) {}
    return null;
  }

  function writeCache(data) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (e) {}
  }

  const getJSON = (path) => fetch(API + path).then((r) => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
  });
  const settle = (promise) => promise.then((value) => value, () => null);

  let loading = null;
  function load() {
    if (loading) return loading;
    const cached = readCache();
    if (cached) return (loading = Promise.resolve(cached));
    loading = Promise.all([
      settle(getJSON('/v1/counts')),
      settle(getJSON('/v2/catalog/games?scope=population')),
      settle(getJSON('/v2/catalog/sets?scope=population&offset=0&limit=5')),
      settle(getJSON('/v1/gradings?limit=200&sort=-completedOn')),
      settle(getJSON('/v1/gradings?limit=4&sort=-completedOn&grade=Flawless'))
    ]).then(([counts, games, sets, feed, flawless]) => {
      const total = counts ? count(counts.totalGraded) : null;
      const month = counts && typeof counts.monthGraded === 'number' ? counts.monthGraded : null;
      const items = selectItems({ counts, games, sets: sets && sets.data, feed: feed && feed.data, flawless: flawless && flawless.data });
      if (!items.length && total === null) throw new Error('No live stats');
      const data = { at: Date.now(), total, month, items, counts: counts || null };
      writeCache(data);
      return data;
    });
    return loading;
  }

  /* ---------- Ticker ---------- */

  function chip(item, icons) {
    const li = document.createElement('li');
    li.className = 'pcg-ticker__item';
    const body = document.createElement(item.href ? 'a' : 'span');
    body.className = 'pcg-ticker__chip';
    if (item.href) {
      body.href = item.href;
      body.target = '_blank';
      body.rel = 'noopener';
    }
    const icon = icons && icons.querySelector('[data-icon="' + item.icon + '"] svg');
    if (icon) body.appendChild(icon.cloneNode(true));
    const part = (cls, value) => {
      if (!value) return;
      const span = document.createElement('span');
      span.className = cls;
      span.textContent = value;
      body.appendChild(span);
    };
    part('pcg-ticker__label', item.label);
    part('pcg-ticker__value', item.value);
    part('pcg-ticker__figure', item.figure);
    part('pcg-ticker__detail', item.detail);
    li.appendChild(body);
    return li;
  }

  function list(items, icons, copy) {
    const ul = document.createElement('ul');
    ul.className = 'pcg-ticker__list';
    ul.setAttribute('role', 'list');
    if (copy) {
      ul.setAttribute('aria-hidden', 'true');
      ul.setAttribute('inert', '');
    }
    items.forEach((item) => {
      ul.appendChild(chip(item, icons));
      const sep = document.createElement('li');
      sep.className = 'pcg-ticker__sep';
      sep.setAttribute('aria-hidden', 'true');
      ul.appendChild(sep);
    });
    return ul;
  }

  function Ticker(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-pcg-ticker-viewport]');
    this.pause = root.querySelector('[data-pcg-ticker-pause]');
    const tpl = root.querySelector('template[data-pcg-ticker-icons]');
    this.icons = tpl ? tpl.content : null;
    this.paused = false;
    this.hovered = false;
    this.focused = false;
    this.items = [];
    this.index = 0;

    if (this.pause) {
      this.pause.addEventListener('click', () => this.setPaused(!this.paused));
    }
    root.addEventListener('mouseenter', () => (this.hovered = true));
    root.addEventListener('mouseleave', () => (this.hovered = false));
    root.addEventListener('focusin', () => (this.focused = true));
    root.addEventListener('focusout', (e) => {
      if (!root.contains(e.relatedTarget)) this.focused = false;
    });
    const rerender = () => this.items.length && this.render(this.items);
    reducedMotion.addEventListener('change', rerender);
    phone.addEventListener('change', rerender);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.mode === 'fade') this.schedule();
    });
  }

  Ticker.prototype.setPaused = function (paused) {
    this.paused = paused;
    this.root.dataset.paused = paused ? 'true' : 'false';
    if (this.pause) {
      const label = paused ? 'Play the live stats' : 'Pause the live stats';
      this.pause.setAttribute('aria-pressed', String(paused));
      this.pause.setAttribute('aria-label', label);
      this.pause.title = label;
    }
  };

  Ticker.prototype.render = function (items) {
    if (!this.viewport) return;
    this.items = items;
    clearTimeout(this.timer);
    this.viewport.textContent = '';
    this.mode = reducedMotion.matches || phone.matches ? 'fade' : 'scroll';
    this.root.dataset.mode = this.mode;
    this.root.classList.add('is-ready');
    if (this.pause) this.pause.hidden = items.length < 2;

    if (this.mode === 'fade') {
      const ul = document.createElement('ul');
      ul.className = 'pcg-ticker__list pcg-ticker__fade';
      ul.setAttribute('role', 'list');
      this.viewport.appendChild(ul);
      this.fadeList = ul;
      this.index = 0;
      this.show();
      this.schedule();
      return;
    }

    const track = document.createElement('div');
    track.className = 'pcg-ticker__track';
    const first = list(items, this.icons, false);
    track.appendChild(first);
    track.appendChild(list(items, this.icons, true));
    this.viewport.appendChild(track);
    // The duration follows the width of one copy, so the speed is the same however many chips there are.
    requestAnimationFrame(() => {
      const width = first.getBoundingClientRect().width;
      const seconds = width > 0 ? Math.max(20, Math.round(width / PIXELS_PER_SECOND / 5) * 5) : 60;
      track.style.animationDuration = seconds + 's';
      track.classList.add('is-running');
    });
  };

  Ticker.prototype.show = function () {
    const item = this.items[this.index % this.items.length];
    this.fadeList.textContent = '';
    if (item) this.fadeList.appendChild(chip(item, this.icons));
    this.fadeList.classList.remove('is-out');
  };

  Ticker.prototype.schedule = function () {
    clearTimeout(this.timer);
    if (this.items.length < 2) return;
    this.timer = setTimeout(() => {
      if (this.paused || this.hovered || this.focused || document.hidden) {
        this.schedule();
        return;
      }
      this.fadeList.classList.add('is-out');
      this.timer = setTimeout(() => {
        this.index = (this.index + 1) % this.items.length;
        this.show();
        this.schedule();
      }, FADE_MS);
    }, FADE_HOLD_MS);
  };

  /* ---------- Live total and drawer summary ---------- */

  function countUp(el, to) {
    const from = el.pcgShown || 0;
    el.pcgShown = to;
    if (reducedMotion.matches || from === to) {
      el.textContent = fmt(to);
      return;
    }
    const span = from ? 900 : 1600;
    let start = null;
    const step = (t) => {
      if (start === null) start = t;
      const p = Math.min(1, (t - start) / span);
      el.textContent = fmt(from + (to - from) * (1 - Math.pow(1 - p, 3)));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  function paintTotals(data) {
    document.querySelectorAll('[data-pcg-live-total]').forEach((el) => {
      if (data.total === null) return;
      const figure = el.querySelector('[data-pcg-live-count]');
      el.setAttribute('aria-label', fmt(data.total) + ' cards graded');
      el.hidden = false;
      if (figure) countUp(figure, data.total);
    });
    document.querySelectorAll('[data-pcg-live-summary]').forEach((el) => {
      if (data.total === null) return;
      el.textContent = fmt(data.total) + ' cards graded' + (data.month ? ' · ' + fmt(data.month) + ' in ' + monthName() : '');
      el.hidden = false;
    });
  }

  /* ---------- Shared counts refresh ---------- */

  const COUNTS_MS = 30000;
  let latest = null;

  function publish(counts) {
    if (!counts) return;
    window.PCGLive.counts = counts;
    document.dispatchEvent(new CustomEvent('pcg:counts', { detail: counts }));
  }

  function refreshCounts() {
    if (document.hidden) return;
    getJSON('/v1/counts').then((counts) => {
      const total = count(counts.totalGraded);
      const month = typeof counts.monthGraded === 'number' ? counts.monthGraded : null;
      if (latest) {
        latest = Object.assign({}, latest, { at: Date.now(), total, month, counts });
        writeCache(latest);
      } else {
        latest = { total, month };
      }
      paintTotals(latest);
      publish(counts);
    }).catch(() => {});
  }

  function init() {
    const roots = Array.from(document.querySelectorAll('[data-pcg-ticker]'));
    const wanted = roots.length || document.querySelector('[data-pcg-live-total], [data-pcg-live-summary], [data-pcg-stats]');
    if (!wanted) return;
    const tickers = roots.map((root) => new Ticker(root));
    load().then((data) => {
      latest = data;
      tickers.forEach((t) => (data.items.length ? t.render(data.items) : (t.root.hidden = true)));
      paintTotals(data);
      publish(data.counts);
      // A cached load can be up to a minute old: fetch the counts now so every figure is current.
      if (!data.counts || Date.now() - data.at > 5000) refreshCounts();
    }).catch(() => {
      // Nothing usable came back: hide the row rather than show an empty or broken one.
      tickers.forEach((t) => (t.root.hidden = true));
      refreshCounts();
    });
    setInterval(refreshCounts, COUNTS_MS);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshCounts(); });
  }

  window.PCGLive = { load, counts: null };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
